/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Execution Output Cache
   Holds intermediate node outputs and tracks allocated disposable resources.
   ═══════════════════════════════════════════════════════════════ */

import type { NodeId } from '../domain/types';
import type { NodeOutputValues } from './types';

export interface DisposableResource {
  dispose(): void;
}

export interface CachedNodeOutput {
  readonly outputs: NodeOutputValues;
  readonly paramHash: string;
  readonly inputHash: string;
  readonly resources: DisposableResource[];
}

export class ExecutionCache {
  private cache = new Map<NodeId, CachedNodeOutput>();

  get(nodeId: NodeId): CachedNodeOutput | undefined {
    return this.cache.get(nodeId);
  }

  set(
    nodeId: NodeId,
    outputs: NodeOutputValues,
    paramHash: string,
    inputHash: string,
    resources: DisposableResource[]
  ): void {
    // If an old entry exists, dispose its previous resources
    this.invalidate(nodeId);
    this.cache.set(nodeId, {
      outputs,
      paramHash,
      inputHash,
      resources,
    });
  }

  invalidate(nodeId: NodeId): void {
    const existing = this.cache.get(nodeId);
    if (existing) {
      for (const res of existing.resources) {
        try {
          res.dispose();
        } catch (e) {
          console.warn(`Error disposing resource for node ${nodeId}:`, e);
        }
      }
      this.cache.delete(nodeId);
    }
  }

  keys(): IterableIterator<NodeId> {
    return this.cache.keys();
  }

  clear(): void {
    for (const nodeId of Array.from(this.cache.keys())) {
      this.invalidate(nodeId);
    }
    this.cache.clear();
  }
}
